import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  output,
  Output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  AsyncValidatorFn,
} from '@angular/forms';
import { AppCommonModule } from '@app/components/app.common.module';
import { Router } from '@angular/router';
import { BrandService } from '../../../brand/brand.service';
import { queryParamsBrand, queryParamsSubBrand } from '../../../financial-setup/model.models';
import { environment } from '@env/environment.local';
import { SubBrandService } from '../../sub-brand.service';
import { NotificationService } from '@app/services/notifications.service';
import { FileType } from '@app/models/fileTypes.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  concatMap,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { FileValidationService } from '@app/services/fileValidation.service';

interface Brand {
  id: number;
  name: string;
}

interface FileValidationConfig {
  maxSize: number;
  allowedTypes: string[];
  allowedExtensions: string[];
  errorMessages: {
    sizeExceeded: string;
    invalidType: string;
  };
}

@Component({
  selector: 'app-sub-brand-template',
  standalone: true,
  templateUrl: './sub-brand-template.component.html',
  styleUrl: './sub-brand-template.component.css',
  imports: [FormsModule, ReactiveFormsModule, AppCommonModule, CommonModule],
})
export class SubBrandTemplateComponent implements OnChanges, OnDestroy {
  @Input() isEdit: boolean = false;
  @Input() subBrandData: any = null;

  @Output() save = new EventEmitter<any>();
  @Output() update = new EventEmitter<any>();

  form: FormGroup;
  brands: Brand[] = [];
  isFormValid: boolean = false;
  file: any;

  hasFile: boolean = false;
  isLoading: boolean = false;

  pageSize: number = environment.pageSize;
  pageIndex: number = 1;

  params: queryParamsBrand = {
    name: null,
    brandCode: null,
    enabled: null,
    pageSize: this.pageSize,
    page: this.pageIndex,
  };

  readonly subBrandImageConfig: FileValidationConfig = {
    maxSize: 5 * 1024 * 1024,
    allowedTypes: [
      'image/jpeg',
      'image/jpg',
      'image/png'
    ],
    allowedExtensions: ['.jpg', '.jpeg', '.png'],
    errorMessages: {
      sizeExceeded: 'El archivo excede el tamaño máximo de 5MB',
      invalidType: 'Solo se permiten archivos JPG o PNG'
    }
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly brandService = inject(BrandService);
  private readonly notificationsService = inject(NotificationService);
  private readonly service = inject(SubBrandService);
  private readonly imageValidationService = inject(FileValidationService);
  readonly close = output<void>();
  readonly fileExist = signal<any>(null);
  readonly defaultFile = computed(() => {
    if (this.isEdit && !this.removedFileExist().isRemoved) {
      return this.fileExist();
    }
    return null;
  });
  readonly removedFileExist = signal<{ isRemoved: boolean; id?: number }>({
    isRemoved: false,
  });
  constructor() {
    this.createForm();
  }
  ngOnDestroy(): void {
    this.resetComponent();
  }

  resetComponent() {
    this.form.reset({
      name: '',
      subBrandCode: '',
      brandId: null,
      enabled: true,
      mimeType: '',
      fileSize: '',
      fileName: '',
    });
    if (this.file) {
      URL.revokeObjectURL(this.file.preview);
      this.file = null;
    }
    this.fileExist.set(null);
    this.hasFile = false;
    this.isFormValid = false;
    this.removedFileExist.set({ isRemoved: false });
  }

  ngOnInit() {
    this.loadBrands();
  }

  ngOnChanges() {
    if (this.isEdit && this.subBrandData) {
      this.updateForm();
    } else if (!this.isEdit && !this.subBrandData) {
      this.resetComponent();
    }
    this.getFile();
  }

  loadBrands() {
    this.isLoading = true;
    this.brandService
      .getAllBrands(this.params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response) => {
          if (response && Array.isArray(response.data)) {
            this.brands = response.data
                .filter((brand: any) => brand.enabled)
              .map((brand: Brand) => ({ id: brand.id, name: brand.name }));
            setTimeout(() => {
              this.isLoading = false;
            }, 3500);
          } else {
            console.error(
              'Expected an array of brands in response.data, but got:',
              response
            );
            setTimeout(() => {
              this.isLoading = false;
            }, 3500);
          }
        },
        (error) => {
          console.error('Error loading brands:', error);
          setTimeout(() => {
            this.isLoading = false;
          }, 3500);
        }
      );
  }

  // Validar nombre único por marca (llamado solo cuando se presiona crear/actualizar)
  private async validateUniqueName(): Promise<boolean> {
    const name = this.form.get('name')?.value;
    const brandId = this.form.get('brandId')?.value;

    if (!name || !brandId) {
      return true;
    }

    // Si estamos editando y el nombre no cambió y la marca no cambió, no validar
    if (this.isEdit && this.subBrandData &&
        name === this.subBrandData.name &&
        brandId === this.subBrandData.brandId) {
      return true;
    }

    // Activar loading durante la validación
    this.isLoading = true;

    const params: queryParamsSubBrand = {
      name: name,
      subBrandCode: null,
      brandId: brandId,
      enabled: null,
      pageSize: 1000,
      page: 1
    };

    try {
      const response = await firstValueFrom(this.service.getAllSubBrands(params));
      const existingSubBrands = response.data || [];
      const hasDuplicate = existingSubBrands.some((subBrand: any) =>
        subBrand.name.toLowerCase() === name.toLowerCase() &&
        subBrand.brandId === brandId &&
        (!this.isEdit || subBrand.id !== this.subBrandData?.id)
      );

      if (hasDuplicate) {
        // Mostrar error en el campo nombre (sin notificación global)
        this.form.get('name')?.setErrors({ duplicateName: true });
        this.isLoading = false; // Desactivar loading
        return false;
      }

      this.isLoading = false; // Desactivar loading
      return true;
    } catch (error) {
      console.error('Error validando nombre único:', error);
      this.isLoading = false; // Desactivar loading en caso de error
      return true; // En caso de error, permitir continuar
    }
  }

  // Validar nombre único con loading (para validación automática con delay)
  private async validateUniqueNameWithLoading(): Promise<boolean> {
    const name = this.form.get('name')?.value;
    const brandId = this.form.get('brandId')?.value;

    if (!name || !brandId) {
      return true;
    }

    // Si estamos editando y el nombre no cambió y la marca no cambió, no validar
    if (this.isEdit && this.subBrandData &&
        name === this.subBrandData.name &&
        brandId === this.subBrandData.brandId) {
      return true;
    }

    // Activar loading durante la validación
    this.isLoading = true;

    const params: queryParamsSubBrand = {
      name: name,
      subBrandCode: null,
      brandId: brandId,
      enabled: null,
      pageSize: 1000,
      page: 1
    };

    try {
      const response = await firstValueFrom(this.service.getAllSubBrands(params));
      const existingSubBrands = response.data || [];
      const hasDuplicate = existingSubBrands.some((subBrand: any) =>
        subBrand.name.toLowerCase() === name.toLowerCase() &&
        subBrand.brandId === brandId &&
        (!this.isEdit || subBrand.id !== this.subBrandData?.id)
      );

      if (hasDuplicate) {
        // Mostrar error en el campo nombre (sin notificación global)
        this.form.get('name')?.setErrors({ duplicateName: true });
        this.isLoading = false; // Desactivar loading
        return false;
      }

      this.isLoading = false; // Desactivar loading
      return true;
    } catch (error) {
      console.error('Error validando nombre único:', error);
      this.isLoading = false; // Desactivar loading en caso de error
      return true; // En caso de error, permitir continuar
    }
  }

  createForm() {
    this.form = this.fb.group({
      name: ['', [Validators.required]], // Removido el validador asíncrono
      subBrandCode: [''],
      brandId: [null, Validators.required],
      enabled: [true],
      mimeType: [''],
      fileSize: [''],
      fileName: [''],
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateFormValidity();
      });

    // Escuchar cambios en el estado de validación del formulario
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        this.updateFormValidity();
      });

    // Escuchar cambios en brandId para revalidar el nombre
    this.form.get('brandId')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newBrandId) => {

        // Limpiar errores previos del nombre cuando cambia la marca
        this.form.get('name')?.setErrors(null);

        // Revalidar el nombre con la nueva marca
        const currentName = this.form.get('name')?.value;
        if (currentName) {
          this.validateUniqueNameWithLoading().then((isValid) => {
            if (!isValid) {
              this.updateFormValidity();
            }
          });
        } else {
          this.updateFormValidity();
        }
      });

    // Escuchar cambios en el nombre para validación con delay
    this.form.get('name')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newName) => {
        // Limpiar errores previos
        this.form.get('name')?.setErrors(null);

        // Validar después de 1 segundo de que el usuario termine de escribir
        setTimeout(() => {
          if (this.form.get('name')?.value === newName) {
            this.validateUniqueNameWithLoading().then((isValid) => {
              if (!isValid) {
                this.updateFormValidity();
              }
            });
          }
        }, 1000);
      });
  }

  updateFormValidity() {
    // Para edición, solo validar nombre y marca
    // Para creación, validar nombre y marca (imagen opcional)
    if (this.isEdit) {
      const nameControl = this.form.get('name');
      const brandControl = this.form.get('brandId');

      const nameValid = nameControl?.valid === true && !nameControl?.errors?.['duplicateName'];
      const brandValid = brandControl?.value !== null && brandControl?.value !== undefined && brandControl?.value !== '';

      this.isFormValid = nameValid && brandValid;

      // console.log('updateFormValidity - Edición:', {
      //   nameValid,
      //   brandValid,
      //   nameStatus: nameControl?.status,
      //   nameErrors: nameControl?.errors,
      //   brandValue: brandControl?.value,
      //   brandStatus: brandControl?.status,
      //   isFormValid: this.isFormValid
      // });
    } else {
      const nameControl = this.form.get('name');
      const brandControl = this.form.get('brandId');

      const nameValid = nameControl?.valid === true && !nameControl?.errors?.['duplicateName'];
      const brandValid = brandControl?.value !== null && brandControl?.value !== undefined && brandControl?.value !== '';

      this.isFormValid = nameValid && brandValid;

      // console.log('updateFormValidity - Creación:', {
      //   nameValid,
      //   brandValid,
      //   fileValid,
      //   nameStatus: nameControl?.status,
      //   nameErrors: nameControl?.errors,
      //   brandValue: brandControl?.value,
      //   brandStatus: brandControl?.status,
      //   isFormValid: this.isFormValid
      // });
    }
  }

  updateForm() {
    if (this.subBrandData) {
      this.form.patchValue({
        name: this.subBrandData.name,
        subBrandCode: this.subBrandData.subBrandCode,
        enabled: this.subBrandData.enabled,
        brandId: this.subBrandData.brandId,
      });

      // Actualizar validación después de cargar los datos
      setTimeout(() => {
        this.updateFormValidity();
      }, 100);
    }
  }

  cancel() {
    this.resetComponent();
    this.close.emit();
  }

  create() {
    this.isLoading = true;
    if (this.form.invalid) {
      console.error('Formulario inválido:', this.form.errors);
      this.isLoading = false;
      return;
    }

    // Validar nombre único antes de proceder
    this.validateUniqueName().then((isValid) => {
      if (!isValid) {
        this.isLoading = false;
        return;
      }

      const subBrandInfo = this.form.value;
      if (this.isEdit) {
        const createAttach$ = this.file ? this.createAttachment() : of(null);
        const operations$ = forkJoin({
          removeImage: this.removedFileExist().isRemoved
            ? this.removeImageFromBackend(this.removedFileExist().id)
            : of(null),
          updateSubBrand: this.service.updateSubBrand(
            this.subBrandData.id,
            subBrandInfo
          ),
        }).pipe(concatMap(() => createAttach$));
        operations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (result) => {
            if (result) {
              subBrandInfo.attachment =
                result.data || this.subBrandData.attachment;
            }
            this.update.emit(subBrandInfo);
            this.resetComponent();
            this.isLoading = false;
          },
          error: (error) => {
            this.notificationsService.showMessage(
              ['Error en la operación'],
              'error'
            );
            this.cancel();
          },
        });
      } else {
        const operations$ = forkJoin({
          createSubBrand: this.service.createSubBrand(subBrandInfo).pipe(
            tap((response) => {
              this.subBrandData = { ...this.subBrandData, id: response.data.id };
              if (this.file) {
                this.createAttachment();
              }
            })
          ),
        });
        operations$
          .pipe(
            concatMap(() => (this.file ? this.createAttachment() : of(null))),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: (response) => {
              this.isLoading = false;
              this.resetComponent();
              this.save.emit(subBrandInfo);
            },
            error: (error) => {
              console.error('Error al crear submarca:', error);
              this.isLoading = false;
            },
          });
      }
    });
  }

  removeImageFromBackend(fileId?: number): Observable<void> {
    if (fileId) {
      return this.service.deleteAttachmentById(fileId).pipe(
        tap(() => {
          this.notificationsService.showMessage(
            ['La imagen se eliminó correctamente.'],
            'success'
          );
          this.clearFileData();
          this.hasFile = false;
        }),
        catchError((error) => {
          console.error('Error al eliminar la imagen:', error);
          this.notificationsService.showMessage(
            ['No se pudo eliminar la imagen. Intente nuevamente.'],
            'error'
          );
          return throwError(() => error);
        })
      );
    } else {
      console.error('No se pudo encontrar un ID válido para el archivo.');
      return of();
    }
  }

  removeImage(): void {
    if (this.isEdit && this.fileExist()) {
      this.removedFileExist.set({ isRemoved: true, id: this.fileExist()?.id });
    }
    this.clearFileData();
    this.hasFile = false;
    // Limpiar también el fileExist signal para que no se muestre la imagen original
    this.fileExist.set(null);
  }

  private clearFileData(): void {
    this.file = null;
    this.form.patchValue({
      fileName: '',
      mimeType: '',
      fileSize: '',
    });
  }

  //#region file
  createAttachment() {
    let file = this.createFormData();
    return this.service.createAttachment(file).pipe(
      tap((response: any) => {
        this.subBrandData.attachment = response.data;
        this.notificationsService.showMessage(
          ['La imagen se añadió correctamente.'],
          'success'
        );
        setTimeout(() => {
          this.notificationsService.clearMessages();
        }, 2000);
      }),
      catchError((error: any) => {
        this.notificationsService.showMessage(
          ['Ha ocurrido un error al subir el archivo. Vuelva a intentarlo.'],
          'error'
        );
        setTimeout(() => {
          this.notificationsService.clearMessages();
        }, 6000);
        return throwError(() => error);
      })
    );
  }

  async onFileSelected(event: any): Promise<void> {
    this.isLoading = true;
    const file: File = event.target.files[0];
    const input = event.target as HTMLInputElement;

    if (file) {
      const isMaxFIleValid = this.imageValidationService.verifyFileSize(file);
      const isFileTypeValid = this.imageValidationService.verifyFileType(file);
      const isAspectRatioValid =
        await this.imageValidationService.verifyAspectRatio(file);

      if (isMaxFIleValid && isFileTypeValid && isAspectRatioValid) {
        this.form.patchValue({
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
        });

        this.file = {
          file: file,
          preview: URL.createObjectURL(file),
        };
        this.isLoading = false;
        this.hasFile = true;
      } else {
        input.value = '';
        const errors = [];
        if (!isMaxFIleValid)
          errors.push('El archivo supera el tamaño máximo permitido (10 MB).');
        if (!isFileTypeValid)
          errors.push('El formato del archivo no es valido (jpeg, png)');
        if (!isAspectRatioValid)
          errors.push(
            'La imagen debe tener una relación de aspecto 3:2, las dimensiones recomendadas son 600x400px o superior.'
          );
        this.notificationsService.showMessage(errors, 'error');
        this.isLoading = false;
        this.hasFile = false;
      }
    }
  }

  createFormData() {
    const { file } = this.file;
    let formData = new FormData();
    formData.append('originalName', file.name);
    formData.append('mimeType', file.type);

    formData.append('elementId', this.subBrandData.id);
    formData.append('typeFile', FileType.SUB_BRAND);
    formData.append('file', file);
    formData.append('fileSize', file.size);
    return formData;
  }

  getFile() {
    if (!this.subBrandData) return;
    this.isLoading = true;
    this.service
      .getAttachmentByElementId(this.subBrandData.id, FileType.SUB_BRAND)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response.data.length > 0) {
            const row = response.data[0];
            const displayUrl = row.publicUrl || row.urlSource;
            this.fileExist.set({ ...row, urlSource: displayUrl });
            this.isLoading = false;
            this.hasFile = true;
          } else {
            this.fileExist.set(null);
            this.isLoading = false;
            this.hasFile = false;
          }
        },
        error: (error) => {
          console.error('Error al obtener la imagen:', error);
          this.isLoading = false;
          this.hasFile = false;
        },
      });
  }
}
